import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Tabs, TextInput, ActionIcon, Tooltip, Loader } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
// Import custom styles for emoji picker
import './emoji-picker-styles.css';

// Import icons individually to avoid linting issues
import {
  // Search and UI icons
  Search, X, Grid3X3,
  // CRM related icons
  Users, User, UserPlus, Building, Phone, Calendar, Bell, BellRing,
  // Document icons
  FileText, Files, ClipboardList, ClipboardCheck, FileSpreadsheet,
  // Business icons
  Briefcase, BarChart, PieChart, TrendingUp, DollarSign, CreditCard, Wallet,
  // Communication icons
  MessageSquare, MessageCircle, Mail, Send, Share,
  // Settings and tools
  Settings, Sliders, Filter,
  // Status icons
  CheckCircle, XCircle, AlertCircle, Clock, Activity,
  // Other useful icons
  Star, Heart, Bookmark, Tag, Link, Globe
} from 'lucide-react';

// Dynamically import emoji-mart components to avoid constructor errors
const EmojiPicker = lazy(() => import('./EmojiPickerWrapper'));

type IconPickerProps = {
  onSelect: (value: { type: 'emoji' | 'built_in', value: string }) => void;
  selectedIcon?: { type: 'emoji' | 'built_in', value: string };
  onClose?: () => void;
  isOpen?: boolean;
};

const IconPicker: React.FC<IconPickerProps> = ({
  onSelect,
  // We still need selectedIcon for the Remove button functionality
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedIcon,
  onClose,
  isOpen = true
}) => {
  const [activeTab, setActiveTab] = useState<string | null>('emoji');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredIcons, setFilteredIcons] = useState<string[]>([]);
  const ref = useClickOutside(() => onClose && onClose());

  // Define icon components outside of the component to prevent re-creation on each render
  // Using any type to avoid TypeScript issues with Lucide icon types
  const iconComponents: Record<string, any> = {
    // People & Organizations
    Users,
    User,
    UserPlus,
    Building,
    Grid3X3,

    // Communication
    Mail,
    Phone,
    MessageSquare,
    MessageCircle,
    Send,
    Share,

    // Documents & Data
    FileText,
    Files,
    ClipboardList,
    ClipboardCheck,
    FileSpreadsheet,

    // Business & Finance
    Briefcase,
    BarChart,
    PieChart,
    TrendingUp,
    DollarSign,
    CreditCard,
    Wallet,

    // Time & Notifications
    Calendar,
    Bell,
    BellRing,
    Clock,

    // Settings & Tools
    Settings,
    Sliders,
    Filter,

    // Status indicators
    CheckCircle,
    XCircle,
    AlertCircle,
    Activity,

    // Other useful icons
    Star,
    Heart,
    Bookmark,
    Tag,
    Link,
    Globe
  };

  // Create the icon names array once, outside the component
  const iconNames = Object.keys(iconComponents);

  // Use useMemo instead of useEffect to compute filtered icons
  // This prevents unnecessary state updates that can cause infinite loops
  const computedFilteredIcons = React.useMemo(() => {
    if (searchQuery.trim() === '') {
      return iconNames;
    }

    // No need for else after return
    return iconNames.filter((name) =>
      name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]); // Only depends on searchQuery since iconNames is now static

  // Update filtered icons when computed value changes
  useEffect(() => {
    setFilteredIcons(computedFilteredIcons);
  }, [computedFilteredIcons]);

  // We'll implement recent icons functionality in the future
  // const recentIcons = [
  //   { type: 'icon', value: 'FileText' },
  //   { type: 'icon', value: 'Briefcase' },
  //   { type: 'icon', value: 'Users' },
  //   { type: 'icon', value: 'Bell' },
  // ];

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className="bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col"
      style={{ zIndex: 1000, width: '350px', maxHeight: '450px', overflow: 'hidden' }}
    >
      {/* Don't show the selected icon at the top to match Pocus design */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value)} className="w-full">
        <div className="flex justify-between items-center px-4 pt-3">
          <Tabs.List>
            <Tabs.Tab value="emoji" className="px-4 py-2 text-sm font-medium">Emoji</Tabs.Tab>
            <Tabs.Tab value="icons" className="px-4 py-2 text-sm font-medium">Icons</Tabs.Tab>
            <Tabs.Tab value="upload" className="px-4 py-2 text-sm font-medium">Upload</Tabs.Tab>
          </Tabs.List>
          <div
            className="text-gray-500 hover:text-gray-700 text-sm font-medium cursor-pointer"
            onClick={() => {
              // Use a default icon when removing to avoid showing the placeholder
              onSelect({ type: 'built_in', value: 'Image' });
              if (onClose) onClose();
            }}
          >
            Remove
          </div>
        </div>

        {activeTab === 'icons' && (
          <div className="px-4 py-3">
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none">
                <Search size={16} color="#94a3b8" />
              </div>
              <TextInput
                placeholder="Filter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mb-2"
                styles={{
                  input: {
                    paddingLeft: '36px',
                    height: '36px',
                    fontSize: '14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px'
                  },
                  section: {
                    width: '30px'
                  }
                }}
                rightSection={searchQuery ? (
                  <ActionIcon onClick={() => setSearchQuery('')} size="sm">
                    <X size={14} />
                  </ActionIcon>
                ) : null}
              />
            </div>
          </div>
        )}

        <Tabs.Panel value="emoji" className="w-full" style={{ overflow: 'hidden' }}>
          {/* Fixed size container to prevent ResizeObserver errors */}
          <div className="h-[350px] w-full" style={{ position: 'relative', overflow: 'hidden' }}>
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader size="sm" /></div>}>
              <EmojiPicker
                onEmojiSelect={(emoji: any) => {
                  onSelect({
                    type: 'emoji',
                    value: emoji.native
                  });
                  // Optionally close the picker after selection
                  // if (onClose) onClose();
                }}
              />
            </Suspense>
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="icons" className="overflow-auto" style={{ maxHeight: '350px' }}>
          <div className="p-2 grid grid-cols-6 gap-2">
            {filteredIcons.length > 0 ? (
              filteredIcons.map((iconName) => {
                const IconComponent = iconComponents[iconName];
                return (
                  <Tooltip key={iconName} label={iconName} position="top" withArrow>
                    <ActionIcon
                      onClick={() => {
                        onSelect({ type: 'built_in', value: iconName });
                        // Don't close the picker after selection
                        // if (onClose) onClose();
                      }}
                      className="w-10 h-10 border border-gray-200 hover:bg-gray-100 rounded-md"
                    >
                      <IconComponent strokeWidth={1.5} width={20} height={20} color="#4b5563" />
                    </ActionIcon>
                  </Tooltip>
                );
              })
            ) : (
              <div className="col-span-6 py-8 text-center text-gray-500">
                No icons found matching your search
              </div>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="upload" className="p-4 w-full" style={{ height: '350px' }}>
          <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <div className="mb-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#94A3B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#94A3B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#94A3B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-sm font-medium">Coming Soon</p>
              <p className="text-gray-400 text-xs mt-1">Custom icon upload will be available soon</p>
            </div>
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

export default IconPicker;
